import { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import { toast } from "react-toastify"
import AsyncSelect from 'react-select/async'
import Server from "../../Shared/Server"
import Spinner from "../../Shared/Spinner"
import { InputField } from "../../Shared/InputField"

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent']
const RESPONSE_TYPES = ['Phone Call', 'Visit', 'Remote Support', 'Other']

const ComplaintForm = () => {
    const [errors, setErrors] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [categories, setCategories] = useState([])

    const [mode, setMode] = useState('existing') // 'existing' | 'walkin'
    const [selectedLocation, setSelectedLocation] = useState(null)
    const [locationInfo, setLocationInfo] = useState(null)
    const [isFetchingLocation, setIsFetchingLocation] = useState(false)

    const [formData, setFormData] = useState({
        category_id: '',
        description: '',
        priority: 'Medium',
        response_type: 'Phone Call',
        due_date: '',
        // walk-in only
        customer_name: '',
        contact_number: '',
        alternative_number: '',
        village_name: '',
        zone_name: '',
    })

    const {
        category_id, description, priority, response_type, due_date,
        customer_name, contact_number, alternative_number, village_name, zone_name,
    } = formData

    const navigate = useNavigate()

    useEffect(() => {
        let isMounted = true
        const getCategories = async () => {
            try {
                const response = await Server.get('/complaint-categories/')
                if (isMounted) setCategories(response?.data || [])
            } catch (_) {}
        }
        getCategories()
        return () => (isMounted = false)
    }, [])

    const onChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const loadLocationOptions = async (query) => {
        try {
            const response = await Server.get('/options/locations', { params: { q: query } })
            return response.data
        } catch (_) {
            return []
        }
    }

    const onLocationSelect = async (option) => {
        setSelectedLocation(option)
        setLocationInfo(null)
        if (!option) return
    }

    const switchMode = (newMode) => {
        setMode(newMode)
        setSelectedLocation(null)
        setLocationInfo(null)
        setErrors({})
    }

    const onSubmit = (e) => {
        e.preventDefault()

        const payload = mode === 'existing'
            ? {
                category_id: category_id || null,
                location_id: selectedLocation?.value || null,
                customer_name: null,
                contact_number: null,
                alternative_number: null,
                village_name: null,
                zone_name: null,
                description,
                priority,
                response_type,
                due_date: due_date || null,
            }
            : {
                category_id: category_id || null,
                location_id: null,
                customer_name,
                contact_number,
                alternative_number: alternative_number || null,
                village_name: village_name || null,
                zone_name: zone_name || null,
                description,
                priority,
                response_type,
                due_date: due_date || null,
            }

        addComplaint(payload)
    }

    const addComplaint = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.post('/complaints/', payload)
            toast.success(response?.data?.detail || 'Complaint created successfully')
            navigate('/complaints/')
        } catch (error) {
            if (error.response?.status === 401) {
                navigate('/login')
            } else if (error.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map(err =>
                    setErrors(prev => ({ ...prev, [err.loc[1]]: err.msg }))
                )
            } else if (error.response?.status === 403) {
                toast.error(error.response.data.detail)
                navigate('/')
            } else {
                toast.error(error.response?.data?.detail || 'Failed to create complaint')
            }
        } finally {
            setIsLoading(false)
        }
    }

    if (isLoading) return <Spinner/>

    return (
        <>
            <div className="card">
                <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">Create Complaint</h3>
                </div>
                <div className="card-body">

                    {/* Mode toggle */}
                    <div className="d-flex justify-content-center mb-4">
                        <div className="btn-group" role="group">
                            <button
                                type="button"
                                className={`btn ${mode === 'existing' ? 'btn-info text-white' : 'btn-outline-info'}`}
                                onClick={() => switchMode('existing')}
                            >
                                Existing Location
                            </button>
                            <button
                                type="button"
                                className={`btn ${mode === 'walkin' ? 'btn-info text-white' : 'btn-outline-info'}`}
                                onClick={() => switchMode('walkin')}
                            >
                                Walk-in
                            </button>
                        </div>
                    </div>

                    <form onSubmit={onSubmit}>

                        {/* ── Existing Location mode ── */}
                        {mode === 'existing' && (
                            <>
                                <div className="row">
                                    <div className="col-md-8">
                                        <div className="form-group">
                                            <label className="form-label">
                                                Location <span className="text-danger ms-1">*</span>
                                            </label>
                                            <AsyncSelect
                                                cacheOptions
                                                defaultOptions
                                                loadOptions={loadLocationOptions}
                                                value={selectedLocation}
                                                onChange={onLocationSelect}
                                                placeholder="Type to search location…"
                                                classNamePrefix="react-select"
                                                className={`react-select-container${errors.location_id ? ' is-invalid' : ''}`}
                                                isClearable
                                            />
                                            {errors.location_id && (
                                                <div className="invalid-feedback d-block">{errors.location_id}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="form-label">Category</label>
                                            <select
                                                className={`form-select${errors.category_id ? ' is-invalid' : ''}`}
                                                name="category_id"
                                                value={category_id}
                                                onChange={onChange}
                                            >
                                                <option value="">— Select Category —</option>
                                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                            </select>
                                            {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
                                        </div>
                                    </div>
                                </div>

                                {/* Read-only customer info */}
                                {isFetchingLocation && (
                                    <div className="text-muted mt-2">Loading location info…</div>
                                )}
                                {locationInfo && !isFetchingLocation && (
                                    <div className="card bg-light mt-3 mb-2">
                                        <div className="card-body py-2">
                                            <div className="row g-2">
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Customer</small>
                                                    <strong>{locationInfo.customer_name || '—'}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Contact</small>
                                                    <strong>{locationInfo.contact_number || locationInfo.location_contact || '—'}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Village</small>
                                                    <strong>{locationInfo.village_name || '—'}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    <small className="text-muted d-block">Zone</small>
                                                    <strong>{locationInfo.zone_name || '—'}</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* ── Walk-in mode ── */}
                        {mode === 'walkin' && (
                            <>
                                <div className="row">
                                    <div className="col-md-4">
                                        <InputField
                                            label="Full Name"
                                            placeholder="Full Name"
                                            name="customer_name"
                                            type="text"
                                            value={customer_name}
                                            onChange={onChange}
                                            error={errors.customer_name}
                                            required="required"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <InputField
                                            label="Contact Number"
                                            placeholder="Contact Number"
                                            name="contact_number"
                                            type="text"
                                            value={contact_number}
                                            onChange={onChange}
                                            error={errors.contact_number}
                                            required="required"
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <InputField
                                            label="Alternative Number"
                                            placeholder="Alternative Number (optional)"
                                            name="alternative_number"
                                            type="text"
                                            value={alternative_number}
                                            onChange={onChange}
                                            error={errors.alternative_number}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-4">
                                        <InputField
                                            label="Village Name"
                                            placeholder="Village Name (optional)"
                                            name="village_name"
                                            type="text"
                                            value={village_name}
                                            onChange={onChange}
                                            error={errors.village_name}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <InputField
                                            label="Zone Name"
                                            placeholder="Zone Name (optional)"
                                            name="zone_name"
                                            type="text"
                                            value={zone_name}
                                            onChange={onChange}
                                            error={errors.zone_name}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="form-label">Category</label>
                                            <select
                                                className={`form-select${errors.category_id ? ' is-invalid' : ''}`}
                                                name="category_id"
                                                value={category_id}
                                                onChange={onChange}
                                            >
                                                <option value="">— Select Category —</option>
                                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                            </select>
                                            {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ── Common fields ── */}
                        <div className="row mt-2">
                            <div className="col-md-12">
                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className={`form-control${errors.description ? ' is-invalid' : ''}`}
                                        name="description"
                                        rows={4}
                                        placeholder="Describe the complaint…"
                                        value={description}
                                        onChange={onChange}
                                    />
                                    {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                                </div>
                            </div>
                        </div>
                        <div className="row mt-2">
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label className="form-label">Priority</label>
                                    <select
                                        className={`form-select${errors.priority ? ' is-invalid' : ''}`}
                                        name="priority"
                                        value={priority}
                                        onChange={onChange}
                                    >
                                        {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                    {errors.priority && <div className="invalid-feedback">{errors.priority}</div>}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label className="form-label">Response Type</label>
                                    <select
                                        className={`form-select${errors.response_type ? ' is-invalid' : ''}`}
                                        name="response_type"
                                        value={response_type}
                                        onChange={onChange}
                                    >
                                        {RESPONSE_TYPES.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    {errors.response_type && <div className="invalid-feedback">{errors.response_type}</div>}
                                </div>
                            </div>
                            <div className="col-md-4">
                                <InputField
                                    label="Due Date"
                                    name="due_date"
                                    type="date"
                                    value={due_date}
                                    onChange={onChange}
                                    error={errors.due_date}
                                    max={null}
                                />
                            </div>
                        </div>

                        <br/>
                        <div className="row">
                            <div className="col-md-12">
                                <input
                                    type="submit"
                                    className="btn btn-outline-info"
                                    value="Save Complaint"
                                    style={{float: 'right'}}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <br/>
        </>
    )
}

export default ComplaintForm
